/**
 * openExternalResourceWindow.js
 *
 * Adult Autism Observation → Task Administration ships stimulus resources
 * (picture cards, cartoons, etc.) as thumbnail links. During a live appointment
 * clinicians want those stimuli in a *separate browser window* so the OBS
 * module stays visible underneath — a plain `target="_blank"` link almost
 * always opens a new *tab*, which stacks behind the current one and is easy
 * to lose mid-session.
 *
 * The web platform can't force a window over a tab (user settings / popup
 * blockers win), but passing window-feature tokens (width/height/…) is the
 * strongest cross-browser signal that asks for a popup window rather than a
 * tab. This module centralises that request so the panel and its tests share
 * one well-defined contract.
 */

/** Default popup chrome — size is large enough for Drive image viewers. */
export const EXTERNAL_RESOURCE_WINDOW_FEATURES =
  'noopener,noreferrer,width=1200,height=800,scrollbars=yes,resizable=yes';

/**
 * Open a URL in a new browser window (popup), rather than a tab.
 *
 * `open` is injectable so unit tests can assert the exact call without
 * stubbing the global `window` object.
 *
 * @param {string} url
 * @param {{ features?: string, open?: typeof window.open }} [options]
 * @returns {Window|null} the opened window, or null if blocked / invalid
 */
export function openExternalResourceWindow(url, options = {}) {
  const {
    features = EXTERNAL_RESOURCE_WINDOW_FEATURES,
    open = typeof window !== 'undefined' && typeof window.open === 'function'
      ? window.open.bind(window)
      : null,
  } = options;

  if (typeof url !== 'string' || !url.trim()) return null;
  if (typeof open !== 'function') return null;

  const win = open(url, '_blank', features);

  // Belt-and-braces against reverse tabnabbing: some browsers historically
  // ignored `noopener` inside the features string. Nulling `opener` after the
  // fact closes that vector when the handle is available.
  if (win) {
    try {
      win.opener = null;
    } catch {
      // Cross-origin / browser policy — safe to ignore.
    }
  }

  return win ?? null;
}

/**
 * Click handler for Task Administration resource anchors.
 *
 * Unmodified primary clicks → open a new window (via
 * {@link openExternalResourceWindow}).
 * Modified clicks (cmd / ctrl / shift / alt) → leave the browser's default
 * `target="_blank"` behaviour alone, so clinicians can still force a new tab
 * when they want one. Same for non-primary buttons (e.g. middle-click).
 *
 * @param {Pick<MouseEvent, 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey' | 'button' | 'preventDefault'>} event
 * @param {string} url
 * @param {{ openWindow?: typeof openExternalResourceWindow }} [options]
 */
export function handleExternalResourceClick(event, url, options = {}) {
  const { openWindow = openExternalResourceWindow } = options;

  if (typeof url !== 'string' || !url.trim()) return;

  // Honour modified clicks — don't steal the browser's new-tab shortcut.
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return;
  }

  // Only intercept primary-button clicks (button === 0). Middle-click and
  // friends should keep default browser behaviour.
  if (typeof event.button === 'number' && event.button !== 0) {
    return;
  }

  event.preventDefault();
  openWindow(url);
}
